import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Typography } from '@mui/material';
import { ReactComponent as Failed } from 'assets/icons/archive-empty.svg';
import { getBillsList } from 'store/bill/actions';
import { resetBillsList } from 'store/bill/reducer';
import { getBillsListSelector } from 'store/bill/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { showToast } from 'store/toastify/reducer';
import { getUserProfileSelector } from 'store/user-service/selectors';
import { BillsListItem } from 'components/Bills';
import { ReceiptPageHeader } from 'components/Receipt';
import { Loader, Observer } from 'components/shared';
import { ScrollToLayoutTopButtonContainer } from 'components/shared/ScrollToLayoutTopButtonContainer';
import { FIREBASE_EVENT_ANALYTICS_BUTTON, FIREBASE_EVENT_ANALYTICS_PAGE } from 'utils/constants';
import { DEFAULT_PAGINATED_BILLS_LIST_REQUEST_BODY } from 'utils/constants/bills';
import { PAYMENT_TYPE, ROUTER_URL } from 'utils/enums';
import { getErrorMessage } from 'utils/helpers';
import { FirebaseAnalytics } from 'utils/services';
import { IBillsListItemResponse, IBillsListRequest, IPaginatedRequest } from 'utils/types';
import styles from './index.module.scss';

export const ServicesBillsListPage: FC = () => {
	const dispatch = useAppDispatch();

	const billsList = useAppSelector(getBillsListSelector);
	const profile = useAppSelector(getUserProfileSelector);

	const history = useHistory();
	const location = useLocation();

	const [reqBody, setReqBody] = useState<IPaginatedRequest<IBillsListRequest>>({
		...DEFAULT_PAGINATED_BILLS_LIST_REQUEST_BODY,
	});

	useEffect(() => {
		return () => {
			dispatch(resetBillsList());
		};
	}, []);

	useEffect(() => {
		if (profile) {
			setReqBody({
				...DEFAULT_PAGINATED_BILLS_LIST_REQUEST_BODY,
				options: {
					...DEFAULT_PAGINATED_BILLS_LIST_REQUEST_BODY.options,
					userId: [profile.userId],
					paymentType: PAYMENT_TYPE.OTHER,
				},
			});
		}
	}, [profile]);

	useEffect(() => {
		if (reqBody.options.userId?.length) {
			handleGetBillsList(reqBody);
		}
	}, [reqBody]);

	const handleGetBillsList = async (reqBody: IPaginatedRequest<IBillsListRequest>): Promise<void> => {
		try {
			await dispatch(getBillsList(reqBody)).unwrap();
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const handleObserve = (): void => {
		setReqBody({ ...reqBody, offset: reqBody.offset + reqBody.limit });
	};

	const handleGoBack = () => {
		FirebaseAnalytics.logEvent(
			FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SERVICES_BILLS_LIST_REPORT,
			FIREBASE_EVENT_ANALYTICS_BUTTON.GO_BACK
		);
		history.push(ROUTER_URL.SERVICES_CATEGORIES);
	};

	return (
		<div className="page">
			<div className="receipt-page-content">
				<ReceiptPageHeader title="Архів платежів" onBackClick={handleGoBack} />
				<ScrollToLayoutTopButtonContainer>
					<div className={styles.content__list}>
						{billsList.data.tableData.length ? (
							<>
								{billsList.data.tableData.map((item: IBillsListItemResponse) => (
									<BillsListItem
										key={item.id}
										data={item}
										onClick={() => history.push(`${location.pathname}/${item.id}`)}
										title={`Платіж №${item.shortenedId}`}
									/>
								))}
								{billsList.data.tableData.length !== billsList.data.listData.totalRows && (
									<Observer onObserve={handleObserve} isLoading={billsList.isLoading} />
								)}
							</>
						) : (
							!billsList.isLoading && (
								<div className={styles.content__list_empty}>
									<Failed className={styles.content__list_empty__icon} />
									<Typography color="--color-neutral-300" variant="h5" marginTop="var(--spacing-7)">
										Архів порожній
									</Typography>
									<Typography textAlign="center" color="--color-neutral-300" variant="body1" marginTop="var(--spacing-4)">
										{/* eslint-disable-next-line react/no-unescaped-entities */}
										Сплачуйте послуги, квитанції з'являться тут
									</Typography>
									<Typography textAlign="center" color="--color-neutral-300" variant="body1" marginTop="var(--spacing-4)">
										А сплачувати карткою HostCard дешевше ;-)
									</Typography>
								</div>
							)
						)}
					</div>
				</ScrollToLayoutTopButtonContainer>
				{billsList.isLoading && !billsList.data.tableData.length && <Loader />}
			</div>
		</div>
	);
};
